import { useEffect, useState } from 'react'
import { Copy, Loader2, RefreshCw, Search, X } from 'lucide-react'
import api from '../../utils/api'

const REASON_OPTIONS = [
  { value: '', label: '全部原因' },
  { value: 'empty_udid', label: '未上传UDID' },
  { value: 'invalid_length', label: 'UDID长度异常' },
  { value: 'invalid_computer_uuid', label: 'UUID/UDID格式异常' },
  { value: 'placeholder_computer_uuid', label: 'UUID命中占位值' },
  { value: 'placeholder_machine_guid', label: 'MachineGuid命中占位值' }
]

export default function InvalidUserDeviceLogsTab() {
  const [loading, setLoading] = useState(false)
  const [packagesLoading, setPackagesLoading] = useState(true)
  const [sourceOptions, setSourceOptions] = useState([])
  const [keyword, setKeyword] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [reason, setReason] = useState('')
  const [installerSource, setInstallerSource] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [records, setRecords] = useState([])
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 })
  const [selectedRawPostBody, setSelectedRawPostBody] = useState(null)
  const [jumpPage, setJumpPage] = useState('')

  const fetchSourceOptions = async () => {
    setPackagesLoading(true)
    try {
      const res = await api.get('/admin/download-package-names')
      if (res.data.success) {
        const data = res.data.data || res.data || {}
        const labels = Array.from(
          new Set((data.list || []).map((item) => String(item?.label || '').trim()).filter(Boolean))
        )
        setSourceOptions(labels)
      }
    } catch (error) {
      console.error('获取异常用户来源列表失败:', error)
    } finally {
      setPackagesLoading(false)
    }
  }

  const fetchRecords = async (page = 1, filters = {}) => {
    const nextKeyword = typeof filters.keyword === 'string' ? filters.keyword : keyword
    const nextReason = typeof filters.reason === 'string' ? filters.reason : reason
    const nextInstallerSource = typeof filters.installerSource === 'string' ? filters.installerSource : installerSource
    const nextStartDate = typeof filters.startDate === 'string' ? filters.startDate : startDate
    const nextEndDate = typeof filters.endDate === 'string' ? filters.endDate : endDate

    setLoading(true)
    try {
      const res = await api.get('/admin/invalid-user-device-logs', {
        params: {
          page,
          limit: pagination.limit,
          keyword: nextKeyword || undefined,
          reason: nextReason || undefined,
          installerSource: nextInstallerSource || undefined,
          startDate: nextStartDate || undefined,
          endDate: nextEndDate || undefined
        }
      })
      if (res.data.success) {
        const data = res.data.data || res.data || {}
        const pager = data.pagination || {}
        setRecords(data.list || [])
        setPagination({
          page: pager.page || 1,
          limit: pager.limit || pagination.limit,
          total: pager.total || 0,
          totalPages: pager.totalPages || 1
        })
      }
    } catch (error) {
      console.error('获取异常用户筛选列表失败:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSourceOptions()
  }, [])

  useEffect(() => {
    if (packagesLoading) return
    fetchRecords(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packagesLoading])

  const handleSearch = (e) => {
    e.preventDefault()
    setKeyword(searchInput)
    fetchRecords(1, { keyword: searchInput })
  }

  const handleReset = () => {
    setSearchInput('')
    setKeyword('')
    setReason('')
    setInstallerSource('')
    setStartDate('')
    setEndDate('')
    setJumpPage('')
    fetchRecords(1, {
      keyword: '',
      reason: '',
      installerSource: '',
      startDate: '',
      endDate: ''
    })
  }

  const handleJump = () => {
    const page = Number(jumpPage)
    if (!jumpPage.trim() || isNaN(page)) {
      setJumpPage('')
      return
    }
    if (page < 1 || page > pagination.totalPages) {
      alert(`请输入 1 ~ ${pagination.totalPages} 之间的页码`)
      return
    }
    fetchRecords(page)
    setJumpPage('')
  }

  const getReasonText = (value) => {
    if (value === 'empty_udid') return '未上传UDID'
    if (value === 'invalid_length') return 'UDID长度异常'
    if (value === 'invalid_computer_uuid') return 'UUID/UDID格式异常'
    if (value === 'placeholder_computer_uuid') return 'UUID命中占位值'
    if (value === 'placeholder_machine_guid') return 'MachineGuid命中占位值'
    return value || '-'
  }

  const getSourceText = (item) => {
    return item.installer_label || item.installer_name || '-'
  }

  const handleCopyRawPostBody = async () => {
    if (!selectedRawPostBody?.raw_post_body) return

    try {
      await navigator.clipboard.writeText(selectedRawPostBody.raw_post_body)
    } catch (error) {
      console.error('复制原始POST数据失败:', error)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">异常用户筛选</h2>
        <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="UID/手机号/设备号/用户名"
              className="w-56 rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            {REASON_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
          <select
            value={installerSource}
            onChange={(e) => setInstallerSource(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            disabled={packagesLoading}
          >
            <option value="">全部来源</option>
            {sourceOptions.map((label) => (
              <option key={label} value={label}>
                {label}
              </option>
            ))}
            <option value="__unmatched__">未匹配来源</option>
            <option value="__empty__">无安装包来源</option>
          </select>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          <button type="submit" className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">
            查询
          </button>
          <button type="button" onClick={handleReset} className="rounded-lg bg-gray-100 px-4 py-2 text-sm text-gray-700 hover:bg-gray-200">
            重置
          </button>
          <button
            type="button"
            onClick={() => fetchRecords(pagination.page)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            刷新
          </button>
        </form>
      </div>

      {loading ? (
        <div className="flex items-center justify-center rounded-xl border border-gray-200 bg-white py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">UID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">手机号</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">用户名</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">异常原因</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">下载来源</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">原始UDID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">POST数据</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">IP</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">时间</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {records.length > 0 ? (
                  records.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">{item.user_id || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{item.phone || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{item.username || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{getReasonText(item.reason)}</td>
                      <td className="max-w-[220px] truncate px-6 py-4 text-sm text-gray-900" title={item.installer_name || undefined}>
                        {getSourceText(item)}
                      </td>
                      <td className="max-w-[240px] truncate px-6 py-4 text-sm text-gray-700" title={item.raw_udid || undefined}>
                        {item.raw_udid || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {item.raw_post_body ? (
                          <button
                            type="button"
                            onClick={() => setSelectedRawPostBody(item)}
                            className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
                          >
                            查看POST数据
                          </button>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{item.ip_address || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{item.created_at || '-'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="px-6 py-10 text-center text-sm text-gray-500">
                      暂无数据
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between bg-gray-50 px-6 py-4">
            <div className="text-sm text-gray-600">
              共 {pagination.total} 条，第 {pagination.page}/{pagination.totalPages} 页
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={jumpPage}
                onChange={(e) => setJumpPage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleJump()}
                placeholder="页码"
                min={1}
                max={pagination.totalPages}
                className="w-16 rounded-lg border border-gray-300 px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <button
                onClick={handleJump}
                className="rounded-lg border border-gray-300 px-3 py-1 text-sm hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"
              >
                跳转
              </button>
              <button
                disabled={pagination.page <= 1}
                onClick={() => fetchRecords(pagination.page - 1)}
                className="rounded-lg border border-gray-300 px-3 py-1 disabled:opacity-50"
              >
                上一页
              </button>
              <button
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => fetchRecords(pagination.page + 1)}
                className="rounded-lg border border-gray-300 px-3 py-1 disabled:opacity-50"
              >
                下一页
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedRawPostBody ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">原始POST数据</h3>
                <p className="mt-1 text-sm text-gray-500">
                  UID: {selectedRawPostBody.user_id || '-'} / 时间: {selectedRawPostBody.created_at || '-'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedRawPostBody(null)}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-3">
              <div className="text-sm text-gray-500">{selectedRawPostBody.request_path || '-'}</div>
              <button
                type="button"
                onClick={handleCopyRawPostBody}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Copy className="h-4 w-4" />
                复制内容
              </button>
            </div>

            <div className="max-h-[70vh] overflow-auto px-6 py-4">
              <pre className="whitespace-pre-wrap break-all rounded-xl bg-gray-50 p-4 text-xs leading-6 text-gray-800">
                {selectedRawPostBody.raw_post_body}
              </pre>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
